import axiosCAPApi from "./axiosCAPApi";
import { Cookies } from "react-cookie";
import { appConfig } from "../helpers/common";

const cookies = new Cookies();

// Lexa calls go through corp-dash-api (LexaProxyController), which adds the
// Apigee API key and BFF secret server-side so they never reach the browser.
export const lexaProxyUrl = `${appConfig.api.baseUrl}/api/lexa`;

export const lexaProxyHeaders = {
  "Content-Type": "application/json",
  lbcOAkey: appConfig.api.lbcOAkey,
};

// The proxy marks responses that came from Lexa; a 401/403 without the marker
// means corp-dash-api rejected the corporate token itself.
export const isLexaUpstreamResponse = (response) =>
  Boolean(response?.headers?.["x-lexa-upstream"]);

let corpRefreshPromise = null;

// Corporate refresh tokens are not rotated, so this can safely run alongside
// the refresh in axiosCAPApiPrivate.
const refreshCorpAccessToken = () => {
  if (!corpRefreshPromise) {
    corpRefreshPromise = axiosCAPApi
      .post("/api/RefreshJWTToken")
      .then((response) => {
        const newAccessToken = response.data;
        cookies.set("accessToken", newAccessToken, {
          secure: true,
          path: "/",
          sameSite: "none",
        });
        return newAccessToken;
      })
      .finally(() => {
        corpRefreshPromise = null;
      });
  }
  return corpRefreshPromise;
};

// Sends the corporate token the proxy requires and refreshes it once when the
// proxy (not Lexa) rejects it.
export const withCorpAuth = (instance) => {
  instance.interceptors.request.use(
    (config) => {
      const accessToken = cookies.get("accessToken");
      if (accessToken && !config.headers["Authorization"]) {
        config.headers["Authorization"] = `Bearer ${accessToken}`;
      }
      // axios clears Content-Type on a FormData request once sent, so a retry would fall back
      // to the JSON default and serialize the file away; keep uploads multipart.
      if (config.data instanceof FormData) {
        config.headers["Content-Type"] = "multipart/form-data";
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const originalRequest = error.config;
      const status = error.response?.status;
      if (
        (status === 401 || status === 403) &&
        !isLexaUpstreamResponse(error.response) &&
        originalRequest &&
        !originalRequest._corpRetry
      ) {
        originalRequest._corpRetry = true;
        return refreshCorpAccessToken().then((newAccessToken) => {
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
          return instance(originalRequest);
        });
      }
      return Promise.reject(error);
    }
  );

  return instance;
};
